import { keyframes } from '@emotion/react';
import { Box, type BoxProps } from '@mui/material';
import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  type DependencyList,
} from 'react';
import { usePrevious } from 'react-use';

const createGlowKeyframes = (color: string) => keyframes`
    0% {
        filter:
                drop-shadow(0 0 2px ${color})
                drop-shadow(0 0 6px ${color});
    }
    100% {
        filter: drop-shadow(0 0 0 rgba(255, 255, 255, 0));
    }
`;

interface GlowFieldProps extends Omit<BoxProps, 'children'> {
  ms?: number;
  startColor?: string;
  children?: ReactNode;
  deps: DependencyList;
  isActive?: boolean;
}

export const GlowField = ({
  ms = 10000,
  startColor = 'rgba(255, 255, 255, 1)',
  children,
  deps,
  isActive = true,
  ...rest
}: GlowFieldProps) => {
  const [isGlowed, setIsGlowed] = useState(false);
  const isMountedRef = useRef(false);
  const glowAnimation = createGlowKeyframes(startColor);
  const prevDeps = usePrevious(deps);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (!isActive) {
      return;
    }

    // Check if triggered by deps change only in react way
    const hasChanged = deps.some(
      (dep, index) => !Object.is(dep, prevDeps?.[index]),
    );
    if (!hasChanged) {
      return;
    }

    const immediateTimeout = setTimeout(() => {
      setIsGlowed(true);
    }, 0);
    const timeout = setTimeout(() => {
      setIsGlowed(false);
    }, ms);
    return () => {
      clearTimeout(immediateTimeout);
      clearTimeout(timeout);
      setIsGlowed(false);
    };
  }, [deps, isActive, ms, prevDeps]);

  return (
    <Box
      {...rest}
      sx={{
        ...(isGlowed && {
          animation: `${glowAnimation} ${ms}ms ease-out forwards`,
        }),
        ...rest.sx,
      }}
    >
      {children}
    </Box>
  );
};
